/**
 * Personal info settings section — profile photo, name, email, timezone, and support.
 */

import { Camera01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo } from 'react';
import type { Key } from 'react-aria-components';

import { TIMEZONE_OPTIONS } from '@/constants/timezones';
import { useOrg } from '@/contexts/OrgContext';
import { useProfile } from '@/hooks/profile/useProfile';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { Input } from '@/lib/base/forms/input/input';
import { Select } from '@/lib/base/forms/select/select';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { TIME_FORMAT_OPTIONS } from '@/lib/time';
import { sanitizeError } from '@/lib/utils/errors';
import { useViewHealthMarker } from '@/lib/view-health';
import { cx } from '@/utils/cx';

import { useUser } from '../../../../allauth_auth';

// Timezone options for the searchable combobox. The empty "Not Set" entry is
// excluded — clearing the combobox represents "not set" instead.
const TIMEZONE_SELECT_ITEMS = TIMEZONE_OPTIONS.filter(
  option => option.value !== ''
).map(option => ({ id: option.value, label: option.label }));

// Select ids must be non-empty, so the blank "use the org default" preference
// is represented by this sentinel and mapped back to '' on save.
const ORG_DEFAULT_TIME_FORMAT_ID = 'org_default';

const TIME_FORMAT_PREFERENCE_ITEMS = [
  { id: ORG_DEFAULT_TIME_FORMAT_ID, label: 'Workspace default' },
  ...TIME_FORMAT_OPTIONS.map(option => ({
    id: option.value,
    label: option.label,
  })),
];

// =============================================================================
// PersonalInfoFormContent — inner component that accesses FormContext
// =============================================================================

interface PersonalInfoFormContentProps {
  profileData: ReturnType<typeof useProfile>['profileData'];
  formData: ReturnType<typeof useProfile>['formData'];
  saving: boolean;
  error: string | null;
  onSave: () => void;
  handleFirstNameChange: (value: string) => void;
  handleLastNameChange: (value: string) => void;
  handleTimezoneChange: (key: Key | null) => void;
  /** Personal time-format preference; '' = use the workspace default. */
  timeFormatPreference: '' | '24h' | '12h';
  /** Saves on selection (membership PATCH) — deliberately not part of the form's Save. */
  handleTimeFormatChange: (key: Key | null) => void;
  timeFormatSaving: boolean;
}

function PersonalInfoFormContent({
  profileData,
  formData,
  saving,
  error,
  onSave,
  handleFirstNameChange,
  handleLastNameChange,
  handleTimezoneChange,
  timeFormatPreference,
  handleTimeFormatChange,
  timeFormatSaving,
}: PersonalInfoFormContentProps) {
  const { setSubmitError } = useFormContext();

  const handleSubmit = useCallback(() => {
    setSubmitError(null);
    onSave();
  }, [onSave, setSubmitError]);

  // Sync API errors from useProfile into FormContext's submitError
  useEffect(() => {
    if (error) {
      setSubmitError(sanitizeError(error));
    }
  }, [error, setSubmitError]);

  return (
    <form noValidate className='space-y-6'>
      {error && (
        <AlertFloating
          color='error'
          title={sanitizeError(error)}
          description=''
        />
      )}

      {/* Email (read-only) */}
      <Input
        label='Email'
        type='email'
        value={profileData?.user?.email ?? ''}
        isDisabled
        size='md'
      />

      {/* First Name and Last Name in grid */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <FormField name='first_name' label='First Name' isRequired>
          {({ isInvalid, showError, inputId }) => (
            <Input
              id={inputId}
              type='text'
              value={formData.first_name}
              onChange={handleFirstNameChange}
              size='md'
              isInvalid={isInvalid}
              showError={showError}
            />
          )}
        </FormField>

        <FormField name='last_name' label='Last Name' isRequired>
          {({ isInvalid, showError, inputId }) => (
            <Input
              id={inputId}
              type='text'
              value={formData.last_name}
              onChange={handleLastNameChange}
              size='md'
              isInvalid={isInvalid}
              showError={showError}
            />
          )}
        </FormField>
      </div>

      {/* Timezone */}
      <Select.ComboBox
        size='sm'
        shortcut={false}
        label='Timezone'
        placeholder='Select a timezone'
        items={TIMEZONE_SELECT_ITEMS}
        selectedKey={formData.timezone ? formData.timezone : null}
        onSelectionChange={handleTimezoneChange}
      >
        {item => (
          <Select.Item id={item.id} textValue={item.label}>
            {item.label}
          </Select.Item>
        )}
      </Select.ComboBox>

      {/* Time format — saves on selection (membership preference), so it
          deliberately has no save button and is not part of the form's Save. */}
      <Select
        size='sm'
        label='Time format'
        hint='Changes how times appear across the app for you.'
        items={TIME_FORMAT_PREFERENCE_ITEMS}
        selectedKey={timeFormatPreference || ORG_DEFAULT_TIME_FORMAT_ID}
        onSelectionChange={handleTimeFormatChange}
        isDisabled={timeFormatSaving}
      >
        {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
      </Select>

      <div className='flex justify-end'>
        <SubmitButton
          onClick={handleSubmit}
          isDisabled={saving}
          isLoading={saving}
          color='primary'
          size='md'
        >
          Save changes
        </SubmitButton>
      </div>
    </form>
  );
}

// =============================================================================
// PersonalInfoSection — outer component with FormProvider
// =============================================================================

export function PersonalInfoSection() {
  const { currentOrg } = useOrg();
  const user = useUser();
  const {
    profileData,
    formData,
    saving,
    uploading,
    uploadSuccess,
    uploadError,
    error,
    updateFormData,
    saveProfile,
    uploadAvatar,
    timeFormatPreference,
    timeFormatSaving,
    setTimeFormatPreference,
  } = useProfile({ orgId: currentOrg?.id, userId: user?.id });
  useViewHealthMarker('settings-section-ready', { ready: !!profileData });

  // Success is transient feedback, so it reuses the helper line rather than
  // adding a card that shifts the layout while it is visible.
  let uploadStatusText = 'Click to change photo';
  if (uploading) {
    uploadStatusText = 'Uploading…';
  } else if (uploadSuccess) {
    uploadStatusText = 'Photo updated';
  }

  const avatarUrl = profileData?.user?.avatar_url ?? '';
  const displayName =
    [formData.first_name, formData.last_name].filter(Boolean).join(' ') ||
    profileData?.user?.email ||
    '';

  const fieldConfig = useMemo(
    () => ({
      first_name: { required: true } as FieldConfig,
      last_name: { required: true } as FieldConfig,
    }),
    []
  );

  const formValues = useMemo(
    () => ({
      first_name: formData.first_name,
      last_name: formData.last_name,
    }),
    [formData.first_name, formData.last_name]
  );

  const handleSaveProfile = useCallback(() => {
    saveProfile().catch(() => {
      // Error handling is managed by useProfile
    });
  }, [saveProfile]);

  const handleAvatarChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        uploadAvatar(file).catch(() => {
          // Error handling is managed by useProfile
        });
      }
      e.target.value = '';
    },
    [uploadAvatar]
  );

  const handleFirstNameChange = useCallback(
    (value: string) => {
      updateFormData({ first_name: value });
    },
    [updateFormData]
  );

  const handleLastNameChange = useCallback(
    (value: string) => {
      updateFormData({ last_name: value });
    },
    [updateFormData]
  );

  const handleTimezoneChange = useCallback(
    (key: Key | null) => {
      updateFormData({ timezone: key !== null ? String(key) : '' });
    },
    [updateFormData]
  );

  const handleTimeFormatChange = useCallback(
    (key: Key | null) => {
      if (key === null) {
        return;
      }
      const value =
        key === ORG_DEFAULT_TIME_FORMAT_ID
          ? ''
          : (String(key) as '24h' | '12h');
      setTimeFormatPreference(value).catch(() => {
        // Error handling is managed by useProfile
      });
    },
    [setTimeFormatPreference]
  );

  return (
    <div className='space-y-8'>
      {/* Section header */}
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>Personal info</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Update your name, photo, and account preferences.
        </p>
      </div>

      {/* Profile Information */}
      <div className='pb-8 border-b border-border-secondary'>
        <div className='flex flex-col lg:flex-row gap-8'>
          {/* Profile Picture - Left Column */}
          <div className='lg:w-48 flex-shrink-0'>
            <div className='text-center'>
              <div className='relative inline-block mb-4'>
                <div className='w-24 h-24 rounded-full overflow-hidden border-2 border-border-secondary'>
                  <img
                    src={avatarUrl || '/static/images/default-avatar.png'}
                    alt={displayName}
                    className='w-full h-full object-cover'
                  />
                </div>
                <label className='absolute -bottom-1 -right-1 w-8 h-8 bg-bg-primary border border-border-secondary rounded-full flex items-center justify-center cursor-pointer hover:bg-bg-secondary_hover transition-colors shadow-sm'>
                  <Camera01 className='w-4 h-4 text-fg-quaternary' />
                  <input
                    type='file'
                    accept='image/*'
                    onChange={handleAvatarChange}
                    disabled={uploading}
                    className='hidden'
                  />
                </label>
              </div>

              {/* One status line rather than an alert card. This column is
                  ~192px wide, so a card wrapped "Avatar uploaded successfully!"
                  onto three lines and shoved the helper text down the page. */}
              <p
                className={cx(
                  'text-xs',
                  uploadError
                    ? 'text-[var(--color-text-error-primary)]'
                    : 'text-fg-quaternary'
                )}
                role={uploadError ? 'alert' : undefined}
              >
                {uploadError ?? uploadStatusText}
              </p>
            </div>
          </div>

          {/* Profile Form - Right Column */}
          <div className='flex-1'>
            <FormProvider fields={fieldConfig} values={formValues}>
              <PersonalInfoFormContent
                profileData={profileData}
                formData={formData}
                saving={saving}
                error={error}
                onSave={handleSaveProfile}
                handleFirstNameChange={handleFirstNameChange}
                handleLastNameChange={handleLastNameChange}
                handleTimezoneChange={handleTimezoneChange}
                timeFormatPreference={timeFormatPreference}
                handleTimeFormatChange={handleTimeFormatChange}
                timeFormatSaving={timeFormatSaving}
              />
            </FormProvider>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div>
        <h3 className='text-sm font-medium text-fg-primary mb-3'>Support</h3>
        <div className='space-y-2'>
          <p className='text-sm text-fg-tertiary'>
            For questions, issues, or assistance with your account, contact{' '}
            <a
              href='mailto:support@ahoy.ai'
              className='text-[var(--color-brand-v2-primary)] hover:text-[var(--color-brand-v2-primary-hover)] underline'
            >
              support@ahoy.ai
            </a>
          </p>
          <p className='text-sm text-fg-tertiary'>
            To delete your account or organization, please email us at the
            address above.
          </p>
        </div>
      </div>
    </div>
  );
}
